// Branching wrapper for /login + its sub-states.
//
// The `useAuthFlow` state machine drives WHICH auth screen renders based on
// `status.kind`, so we don't need separate routes per state — react-router
// just lands the user at /login and this component switches internally.
// Keeps the URL stable while the user steps through the auth flow (which is
// what the designer spec wants — see auth-handoff state-machine diagram).

import type { JSX } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/index.js';
import { LoginScreen } from './auth/LoginScreen.js';
import { FirstLoginPasswordScreen } from './auth/FirstLoginPasswordScreen.js';
import { MFAChallengeScreen } from './auth/MFAChallengeScreen.js';
import { MFARecoveryCodeScreen } from './auth/MFARecoveryCodeScreen.js';
import { MFASetupWizard } from './auth/MFASetupWizard.js';

export function AuthRoutes(): JSX.Element | null {
  const { status } = useAuth();

  switch (status.kind) {
    case 'idle':
      // Bootstrap from sessionStorage in progress — render nothing rather
      // than flash a screen the user is about to be navigated away from.
      return null;
    case 'authenticated':
      // Already signed in — bounce back to the app root.
      return <Navigate to="/" replace />;
    case 'mfa-challenge':
    case 'submitting-mfa':
      return <MFAChallengeScreen />;
    case 'new-password-required':
    case 'submitting-new-password':
      return <FirstLoginPasswordScreen />;
    // MFA recovery via Custom Auth Flow — user receives a 6-digit code by
    // email, enters it, Cognito issues tokens, we immediately walk them
    // through fresh TOTP enrollment in the wizard below.
    case 'mfa-recovery-code-sent':
    case 'submitting-mfa-recovery-code':
      return <MFARecoveryCodeScreen />;
    // mfa-reenrollment-pending + submitting-mfa-reenrollment reuse the
    // same setup wizard UI (QR + code input); the wizard routes "submit
    // code" to verifyMfaReenrollment based on the active status kind.
    case 'mfa-setup-required':
    case 'mfa-setup-pending':
    case 'submitting-mfa-verify':
    case 'mfa-reenrollment-pending':
    case 'submitting-mfa-reenrollment':
      return <MFASetupWizard />;
    case 'unauthenticated':
    case 'submitting-login':
    default:
      return <LoginScreen />;
  }
}
